"use client";

import { Landmark } from "lucide-react";
import { useEffect, useState } from "react";
import { useFacilityService } from "@/lib/facility-service";

/* The facility's regulatory body, shown wherever the facility is (sidebar,
   dashboard, My facility, the onboarding review). "Pharmacy and Poisons Board
   (PPB)" shortens to "PPB" where space is tight; the full name stays in the title. */

export const regulatorShortName = (name: string) => name.match(/\(([A-Z][A-Za-z]{1,9})\)\s*$/)?.[1] ?? name;

export function RegulatorBadge({ name, tone = "light", short = false }: { name: string; tone?: "light" | "dark"; short?: boolean }) {
  const label = short ? regulatorShortName(name) : name;
  return <span className={`regulator-badge ${tone}`} title={`Regulated by ${name}`}>
    <Landmark size={12} aria-hidden="true" /><span className="regulator-badge-prefix">Regulated by</span>{" "}<strong>{label}</strong>
  </span>;
}

/* One lookup per facility per page load: the full registry record holds the
   regulator; the local record is the fallback (and the demo source). */
const regulatorCache = new Map<string, Promise<string>>();

export function useFacilityRegulator(facilityId: string) {
  const service = useFacilityService();
  const [regulator, setRegulator] = useState("");
  useEffect(() => {
    setRegulator("");
    if (!facilityId) return;
    let active = true;
    let lookup = regulatorCache.get(facilityId);
    if (!lookup) {
      lookup = service.facilityRegistryRecord(facilityId)
        .then((registry) => registry?.facility.regulatory_body?.trim() || service.facilityDetails(facilityId).then((details) => details.facility.regulatory_body?.trim() ?? ""))
        .catch(() => "");
      regulatorCache.set(facilityId, lookup);
    }
    lookup.then((value) => {
      if (!value) regulatorCache.delete(facilityId);
      if (active) setRegulator(value);
    });
    return () => { active = false; };
  }, [service, facilityId]);
  return regulator;
}
